import { Endpoints, createEndpointsConfig } from "./endpoints"
import { PageSettings } from "./pageSettings"
import { SessionConfig } from "./sessionConfig"
import { SiteOwnerSettings, createSiteOwnerSettings } from "./siteOwnerSettings"

export type Geo3 = {
  /**
   * Continent
   */
  con: string
  /**
   * Country
   */
  c: string
  /**
   * State
   */
  s: string
  /**
   * ASN
   */
  a: number
}

export type ConfigSettings3 = {
  /**
   * App events interval in seconds
   */
  a: number
  /**
   * Config refresh interval in seconds
   */
  c: number
  /**
   * DNS client info base hostname
   */
  dcibh: string
  /**
   * DNS client info sample percentage. 0 means never. 1 means always.
   */
  dcisp: number
  /**
   * Max initial measurements
   */
  m: number
  /**
   * Disallowed networks
   */
  n: number[]
  /**
   * Scheduler run interval in seconds
   */
  sc: number
  /**
   * Start delay in seconds
   */
  st: number
  /**
   * Sentinel detection sample rate (0.0-1.0). Optional.
   */
  sdsr?: number
  /**
   * Sentinel detection failure threshold (0.0-1.0). Optional.
   */
  sdft?: number
  /**
   * Sentinel detection per-cycle probe count cap (#849). Caps the number of
   * sentinels probed per detection cycle, decoupling per-session probe count
   * from sentinel population size. Omitted/0 means no cap (use full eligible
   * sentinel set), preserving the pre-#849 behavior for forward
   * compatibility while the Go-side `sdpc` global is rolled out.
   */
  sdpc?: number
}

type POPIDDetectionConfig = {
  /**
   * The name of the response header to read.
   */
  h: string
  /**
   * An optional regex used to extract the POP ID from the response header.
   */
  r?: string
  /**
   * Zero or more request headers that unlock the expected response header.
   */
  rh?: Record<string, string>
}

export type TestObject3 = {
  /**
   * Add random subdomain token
   */
  a: boolean
  /**
   * Platform ID
   */
  i: string
  /**
   * Pulsar App ID
   */
  pa: string | null
  /**
   * Pulsar Job ID
   */
  pj: string | null
  /**
   * Send to Doppler
   */
  d: boolean
  /**
   * Send to Pulsar
   */
  p: boolean
  /**
   * Size in bytes
   */
  s: number
  /**
   * Target frequency (measurements per hour)
   */
  f: number
  /**
   * Timeout in seconds
   */
  t: number
  /**
   * Test object URL
   */
  u: string
  /**
   * Used for decisioning
   */
  us: boolean
  /**
   * POP ID detection configuration
   */
  po?: POPIDDetectionConfig
  /**
   * Cold start download enabled
   */
  c?: boolean
  /**
   * Post-cold start download delay in seconds (default: 4 seconds)
   */
  cd?: number
  /**
   * Cold-start warm-up object path, emitted by rum-config-gen for clients
   * >= v4.11.0. The client warms the connection by fetching this small object
   * on the measurement host, so the measurement itself runs on a warm
   * connection without re-downloading the large measurement object. It is a
   * path, not a full URL: the client swaps it onto the measurement host,
   * preserving the random-subdomain token. A cold-start platform whose config
   * omits this path is malformed and is excluded from every measurement
   * population (see platformTestObjects.filterMisconfiguredColdStartPlatforms).
   */
  cu?: string
  /**
   * Sentinel platform flag. Indicates this test object is a sentinel for
   * tracker blocking detection.
   */
  sp?: boolean
}

export type Config3Source = {
  /**
   * Geo settings
   */
  g: Geo3
  /**
   * Array of test object settings
   */
  o: TestObject3[]
  /**
   * Global settings
   */
  s: ConfigSettings3
  /**
   * Response updated at
   */
  u: string
  /**
   * Config version
   */
  v: string
}

export const createConfig3: (
  source: Config3Source,
  pageSettings: PageSettings,
) => SessionConfig = (s, p) => new Config3Impl(s, p)

class Config3Impl implements SessionConfig {
  private _clientEndpoints: Endpoints
  private _siteOwnerSettings: SiteOwnerSettings
  constructor(source: Config3Source, pageSettings: PageSettings) {
    /* eslint-disable @typescript-eslint/dot-notation */
    const settings = source["s"]
    this._siteOwnerSettings = createSiteOwnerSettings(settings, pageSettings)
    /* eslint-enable @typescript-eslint/dot-notation */
    this._clientEndpoints = createEndpointsConfig(
      {
        // Application-event reporting is intentionally disabled in production
        // as part of the VDE-3712 spin-down (PR #478, Aug 2024). This is the
        // durable steady state, not a temporary measure -- do not "fix" by
        // wiring an endpoint here. Non-prod and test pages can still opt in
        // via pageSettings.applicationLoggingEndpoint (see endpoints.ts and
        // components/terraform/instance/extras/js-client/test.html.tftpl),
        // which overrides this null at runtime. See GH #842 for the analysis
        // affirming the closed-state decision.
        application_events_base: null,
        doppler: {
          resource_timing: "https://doppler-beacon.cbsivideo.com/log",
        },
        pulsar: "https://b.ns1p.net?v=1583182801",
      },
      pageSettings,
    )
  }

  get siteOwnerSettings() {
    return this._siteOwnerSettings
  }

  get beaconEndpointInfo() {
    return this._clientEndpoints
  }
}
